'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  FiAward, 
  FiClock, 
  FiActivity, 
  FiCheckCircle, 
  FiBriefcase,
  FiZap,
  FiPlus,
  FiRefreshCcw
} from 'react-icons/fi'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import PlanSelectionModal from '@/components/PlanSelectionModal'

export default function PlanPage() {
  const [summary, setSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [usuario, setUsuario] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [uRes, sRes] = await Promise.all([
        fetch('/api/auth/verificar').then(r => r.json()),
        fetch('/api/user/summary').then(r => r.json())
      ])

      if (uRes.success) setUsuario(uRes.usuario)
      if (sRes.success) setSummary(sRes.data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  const currentPlanId = Number(summary?.planId || 1)

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-gray-900">Mi Plan y Suscripción</h1>
          <p className="text-gray-500">Gestiona y mejora los límites de tu cuenta.</p>
        </div>
        <button 
          onClick={fetchData}
          className="p-3 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-blue-600 hover:shadow-md transition-all"
        >
          <FiRefreshCcw className={cn("w-5 h-5", loading && "animate-spin")} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Plan Card */}
        <Card className="lg:col-span-1 border-none shadow-sm overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 text-white relative">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <FiAward className="w-32 h-32" />
          </div>
          <CardContent className="p-8 h-full flex flex-col justify-between">
            <div className="space-y-6">
              <div className="px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-[10px] font-extrabold uppercase tracking-widest inline-block border border-white/10">
                Suscripción Actual
              </div>
              <div>
                <h2 className="text-3xl font-bold uppercase">{summary?.nombrePlan || 'Gratuito'}</h2>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-5xl font-black">S/ {summary?.precioPlan?.toString() || '0'}</span>
                  <span className="text-blue-100 font-medium">/ mensual</span>
                </div>
              </div>
              <p className="text-blue-100 text-sm leading-relaxed max-w-xs">
                Acceso completo a nuestra infraestructura API con límites escalables según tu crecimiento.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <button 
                onClick={() => setModalOpen(true)}
                className="w-full py-4 bg-white text-blue-700 rounded-2xl font-bold shadow-2xl hover:bg-blue-50 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
              >
                <FiPlus className="w-5 h-5" />
                Cambiar de Plan
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Plan Stats */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-none shadow-sm group hover:shadow-md transition-all">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-4 bg-blue-50 rounded-2xl text-blue-600 group-hover:bg-blue-100 transition-colors">
                <FiZap className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Capacidad Mensual</p>
                <div className="text-xl font-bold text-gray-900">
                  {summary?.consultasMensuales === -1 ? '∞ Ilimitado' : `${summary?.consultasMensuales?.toLocaleString()} consultas`}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm group hover:shadow-md transition-all">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-4 bg-purple-50 rounded-2xl text-purple-600 group-hover:bg-purple-100 transition-colors">
                <FiActivity className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Consumo Actual</p>
                <div className="text-xl font-bold text-gray-900">
                  {summary?.consultasRealizadas?.toLocaleString()} utilizadas
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm group hover:shadow-md transition-all">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-4 bg-green-50 rounded-2xl text-green-600 group-hover:bg-green-100 transition-colors">
                <FiClock className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Próxima Renovación</p>
                <div className="text-xl font-bold text-gray-900">
                  {summary?.fechaRenovacion ? format(new Date(summary.fechaRenovacion), 'dd/MM/yyyy') : 'Sin fecha'}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm group hover:shadow-md transition-all">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-4 bg-cyan-50 rounded-2xl text-cyan-600 group-hover:bg-cyan-100 transition-colors">
                <FiCheckCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Estado del Plan</p>
                <div className={cn(
                  "text-xl font-bold",
                  summary?.estadoPlan === 'Activo' ? "text-green-600" : "text-red-500"
                )}>
                  {summary?.estadoPlan || 'Inactivo'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Info Card */}
          <Card className="md:col-span-2 border-none shadow-sm overflow-hidden">
             <CardHeader className="bg-gray-50/50 border-b p-6">
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                    <FiBriefcase className="text-blue-500" /> Historial de Plan
                </CardTitle>
             </CardHeader>
             <CardContent className="p-0">
                <div className="p-6 text-sm text-gray-500">
                    Tu cuenta fue registrada inicialmente bajo el <strong>Plan Gratuito</strong> de bienvenida.
                </div>
             </CardContent>
          </Card>
        </div>
      </div>

      <PlanSelectionModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        currentPlanId={currentPlanId}
        userEmail={usuario?.email || ''}
      />
    </div>
  )
}
